import React from 'react';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { bffQuery } from '../lib/bffClient';
import { useTranslation } from '../i18n';
import { useOptionalClinicContext } from '../context/ClinicContext.jsx';
import { useClinicCapabilities } from './useClinicCapabilities';
import { countryForPhone } from '../constants/countries';
import { countryForCurrency, fetchAccountRegion } from '../screens/Onboarding/accountRegion.js';

/**
 * Where the ACTIVE CLINIC is — so registering a patient at a clinic abroad does
 * not start the mobile on Iran's +98, offer Iran's insurers, or hold a national
 * id to Iran's ten-digit کد ملی (PM-0061).
 *
 * The onboarding wizard asks where the FOUNDER's number is from
 * (accountRegion.js). A registration form needs the clinic's own answer — a
 * receptionist is not the founder — so it is read off what the clinic said about
 * itself, strongest first:
 *
 *   0. the COUNTRY it stored (PM-0082 / PM-0218), when that is not Iran. `IR` is
 *      also what every clinic that predates the field reads — Iraqi ones that
 *      never set theirs included — so it does not overrule 1–3 below: an `IR`
 *      clinic is answered exactly as before, and an Iraqi one that never set its
 *      country keeps today's answer until it does.
 *   1. its currency, when that names one country. IRR is Iran — every clinic
 *      before the choice existed, and a founder from abroad is never offered it
 *      — and IQD is Iraq. Answered at once, with no request: it rides on the
 *      membership the panel booted with.
 *   2. otherwise (USD, EUR, CHF — currencies many countries keep) its office
 *      line, E.164 since «مشخصات کلینیک» started writing it;
 *   3. otherwise the signed-in account's own number, as the onboarding reads it.
 *
 * `iran` is true only for an Iranian answer from 1 or 2. The account's number
 * may pick the country a mobile STARTS on, but it never puts a non-IRR clinic
 * back on Iran's rules: an Iranian receptionist at a clinic abroad still
 * registers that clinic's patients. While 2 and 3 are on their way a non-IRR
 * clinic reads `{ iran: false, country: null }` — no Iranian defaults to flash
 * and then take back.
 *
 * Under a brand outside the Iranian market (`market: 'intl'`) no clinic is in
 * Iran: `iran` is always false, and an answer that would have been Iran — an IRR
 * currency, a +98 office line — is "country unknown" instead.
 *
 * `insuranceCatalog` is the clinic's insurer catalogue from its capabilities —
 * the country whose insurers it is offered, null for «آزاد» alone, and
 * `undefined` while the capabilities are not known (lib/insurance.js
 * `registrationInsurers` reads it).
 */

const CLINIC_LINE = `
  query ClinicOfficeLine {
    myClinic { id officePhone }
  }
`;

// One answer per clinic and one per account, for the session: the booking modal
// opens on every F4 and none of this changes between two bookings. A failed read
// is forgotten rather than cached, so the next form asks again.
const lines = new Map();
let account = null;

function officeLineCountry(clinicId) {
  if (!lines.has(clinicId)) {
    lines.set(clinicId, bffQuery(CLINIC_LINE, {}, 'ClinicOfficeLine')
      .then((data) => countryForPhone(data?.myClinic?.officePhone)?.iso2 || null)
      .catch(() => { lines.delete(clinicId); return null; }));
  }
  return lines.get(clinicId);
}

function accountCountry() {
  if (!account) {
    account = fetchAccountRegion()
      // Never Iran, whatever the number: see the header for why the account
      // only ever picks a starting country, and only a foreign one.
      .then((answer) => (answer.foreign && answer.country !== 'IR' ? answer.country : null))
      .catch(() => { account = null; return null; });
  }
  return account;
}

/** Tests only: forget every answer. */
export function resetClinicRegion() {
  lines.clear();
  account = null;
}

// A stored country that says something 1–3 cannot: an assigned code other than Iran's.
const storedForeignCountry = (caps) => {
  const code = caps?.country;
  return typeof code === 'string' && /^[A-Z]{2}$/.test(code) && code !== 'IR' ? code : null;
};

/**
 * `{ iran, country, insuranceCatalog }` — `country` an iso2 code, or null while
 * unknown; `insuranceCatalog` see the header.
 */
export function useClinicRegion() {
  const clinic = useOptionalClinicContext();
  const caps = useClinicCapabilities();
  // The clinic's currency from its context; outside one (a component under test)
  // the currency the i18n layer is bound to, which ClinicContext sets from the
  // same membership in the app.
  const { currency: bound } = useTranslation();
  const currency = clinic?.currency || bound || 'IRR';
  const clinicId = clinic?.activeMembership?.clinicId || '';
  const iranMarket = isIranMarket();
  const fromCurrency = countryForCurrency(currency);
  const named = storedForeignCountry(caps) || (!iranMarket && fromCurrency === 'IR' ? null : fromCurrency);
  const insuranceCatalog = caps ? (caps.insuranceCatalog ?? null) : undefined;
  const key = `${clinicId}|${currency}`;
  const [looked, setLooked] = React.useState(null);

  React.useEffect(() => {
    if (named) return undefined;
    let live = true;
    (async () => {
      const country = (await officeLineCountry(clinicId)) || (await accountCountry());
      if (live) setLooked({ key, country });
    })();
    return () => { live = false; };
  }, [named, clinicId, key]);

  return React.useMemo(() => {
    const found = named || (looked?.key === key ? looked.country : null);
    // 'IR' can only have come from the currency or the office line — the
    // account's answer is never Iran (`accountCountry`).
    const country = !iranMarket && found === 'IR' ? null : found;
    return { iran: country === 'IR', country, insuranceCatalog };
  }, [named, looked, key, iranMarket, insuranceCatalog]);
}

export default useClinicRegion;
